import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { UsersService } from 'src/app/services/users.service';
import { BuildingsService } from 'src/app/services/buildings.service';
import { RequirementsService } from 'src/app/services/requirements.service';
import { ToastService } from 'src/app/services/toast.service';
import { BulkUserUpdateService } from 'src/app/bulk-user-update/bulk-user-update.service';
import { AuthService } from 'src/app/services/auth.service';
import { RolesService } from 'src/app/services/roles.service';
import { environment } from 'src/environments/environment';
import UserGenerator from 'src/app/classes/user.class';

@Component({
  selector: 'app-reassign-staff',
  templateUrl: './reassign-staff.component.html',
  styleUrls: ['./reassign-staff.component.scss'],
})
export class ReassignStaffComponent implements OnInit, OnDestroy {
  selectedReassignmentType: any = null;
  showBuildingReassignment = false;
  showRequirementReassignment = false;
  showPrimaryManagerReassignment = false;
  showSecondaryManagerReassignment = false;
  showUserRolesReassignment = false;

  // Building workflow properties
  selectedNewBuilding: any = null; // Step 1: New building to assign
  selectedFilterBuilding: any = null; // Step 2: Building to filter staff from
  assignmentType: 'move' | 'add' = 'move'; // Assignment type: move or add

  // Primary Manager workflow properties
  selectedNewManager: any = null; // Step 1: New manager to assign
  selectedFilterManager: any = null; // Step 2: Manager to filter staff from
  managerAssignmentType: 'move' | 'add' = 'move'; // Assignment type: move or add

  // Secondary Manager workflow properties
  selectedNewSecondaryManager: any = null; // Step 1: New secondary manager to assign
  selectedFilterSecondaryManager: any = null; // Step 2: Secondary manager to filter staff from

  // User Roles workflow properties
  selectedFilterRole: any = null; // Step 1: Role to filter users by
  selectedNewRole: any = null; // Step 2: New role to assign
  availableRoles: any[] = []; // Available roles for assignment

  // Requirement workflow properties
  selectedRequirement: any = null; // Selected requirement to assign
  requirements: any[] = []; // Available requirements

  // User list properties
  userList: any[] = [];
  filteredUserList: any[] = [];
  selectedUsers: any[] = [];
  userSearchText = '';
  selectAllUsers = false;

  // Buildings properties (for dropdown)
  buildings: any[] = [];

  // Managers properties (for dropdown)
  managers: any[] = [];
  secondaryManagers: any[] = [];

  // Progress tracking
  isProcessing = false;
  progressMessage = '';
  progressPercentage = 0;
  estimatedTimeRemaining = 0;
  isLoadingStaff = false;

  // Helper method for template
  Math = Math;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private router: Router,
    private usersService: UsersService,
    private buildingsService: BuildingsService,
    private requirementsService: RequirementsService,
    private toastService: ToastService,
    private bulkUserUpdateService: BulkUserUpdateService,
    private authService: AuthService,
    private rolesService: RolesService
  ) {}

  reassignmentOptions = [
    {
      icon: 'home_work',
      title: 'New Building',
      description:
        'Add a new building to users, or move users from one building to another.',
    },
    {
      icon: 'person_add',
      title: 'New Primary Manager',
      description: 'Assign a new primary manager to users.',
    },
    {
      icon: 'group_add',
      title: 'New Secondary Manager',
      description: 'Assign a new secondary manager to users.',
    },
    {
      icon: 'build',
      title: 'New Requirement',
      description: 'Assign a new requirement to users.',
    },
    {
      icon: 'admin_panel_settings',
      title: 'User Roles',
      description: 'Assign users to a new role.',
    },
  ];

  ngOnInit(): void {
    this.loadManagersFromApproverReference();
    this.loadSecondaryManagers();
    this.loadBuildings();
    this.loadRequirements();
  }

  selectReassignmentType = (option: any) => {
    this.selectedReassignmentType = option;

    // Reset all workflow flags
    this.showBuildingReassignment = false;
    this.showRequirementReassignment = false;
    this.showPrimaryManagerReassignment = false;
    this.showSecondaryManagerReassignment = false;
    this.showUserRolesReassignment = false;

    if (option.title === 'New Building') {
      this.showBuildingReassignment = true;
      this.loadBuildings(); // Load buildings for dropdown
    } else if (option.title === 'New Primary Manager') {
      this.showPrimaryManagerReassignment = true;
      this.loadManagersFromApproverReference(); // Load managers for dropdown
    } else if (option.title === 'New Secondary Manager') {
      this.showSecondaryManagerReassignment = true;
      this.loadSecondaryManagers(); // Load secondary managers for dropdown
    } else if (option.title === 'New Requirement') {
      this.showRequirementReassignment = true;
      this.loadRequirements(); // Load requirements for dropdown
    } else if (option.title === 'User Roles') {
      this.showUserRolesReassignment = true;
      this.loadAvailableRoles(); // Load roles for dropdown
    }
  };

  goBack = () => {
    this.selectedReassignmentType = null;
    this.showBuildingReassignment = false;
    this.showRequirementReassignment = false;
    this.showPrimaryManagerReassignment = false;
    this.showSecondaryManagerReassignment = false;
    this.showUserRolesReassignment = false;
    this.resetBuildingReassignment();
    this.resetRequirementReassignment();
    this.resetPrimaryManagerReassignment();
    this.resetSecondaryManagerReassignment();
    this.resetUserRolesReassignment();
  };

  // Load buildings
  loadBuildings = () => {
    const buildingSubscription = this.buildingsService.buildingsData$.subscribe(
      (buildingData: any) => {
        if (buildingData?.rows) {
          this.buildings = buildingData.rows;
        }
      }
    );
    this.subscriptions.push(buildingSubscription);

    // Get active buildings
    this.buildingsService.getBuildings(1000, 0); // Large limit to get all buildings
  };

  // Load requirements
  loadRequirements = () => {
    const requirementsSubscription =
      this.requirementsService.requirementsData$.subscribe(
        (requirementsData: any) => {
          if (requirementsData?.rows) {
            this.requirements = requirementsData.rows;
          }
        }
      );
    this.subscriptions.push(requirementsSubscription);

    // Get all active requirements with children included (same as edit staff)
    this.requirementsService.getRequirementsChildrenIncluded();
  };

  // Load available roles for User Roles reassignment
  loadAvailableRoles = () => {
    // Get all available roles from the system using RolesService
    this.rolesService.getRoles();
    this.rolesService.rolesData$.subscribe((rolesData: any) => {
      if (rolesData?.rows) {
        // Map the roles data to the format expected by the component
        this.availableRoles = rolesData.rows
          .map((role: any) => ({
            roleName: role.roleName,
            displayName: role.roleName,
            id: role.id,
          }))
          .sort((a: any, b: any) => a.roleName.localeCompare(b.roleName));

        console.log('Available roles with IDs:', this.availableRoles);
      }
    });
  };

  // Load managers (legacy method - no longer used)
  loadManagers = () => {
    // Load all users who can be managers (users with management authority)
    const userSubscription = this.usersService.usersData$.subscribe(
      (userData: any) => {
        if (userData?.rows) {
          // Filter users who can be managers (active users with management authority)
          const allActiveUsers = userData.rows.filter(
            (user: any) => user.activeFlag === 1
          );

          this.managers = allActiveUsers.filter((user: any) => {
            // Include users with specific management roles or authority levels
            const hasManagementRole = user.Roles?.some((role: any) => {
              const roleName = role.roleName?.toLowerCase() || '';
              const accessLevel = role.accessLevel || '';

              return (
                // Primary authority levels
                accessLevel === 'MANAGE_ALL_AUTHORITY' ||
                accessLevel === 'MANAGE_PRIMARY_AUTHORITY' ||
                accessLevel === 'MANAGE_SECONDARY_AUTHORITY' ||
                // Specific role names that indicate management
                roleName.includes('admin') ||
                roleName.includes('supervisor') ||
                roleName.includes('manager') ||
                roleName.includes('director') ||
                roleName.includes('lead') ||
                roleName.includes('head') ||
                roleName.includes('chief') ||
                // Include default roles
                roleName.includes('default') ||
                // Include any role that has management in the name
                roleName.includes('management') ||
                // Include custom roles (any role that's not Teacher)
                (roleName !== 'teacher' && roleName.length > 0)
              );
            });

            return hasManagementRole;
          });
        }
      }
    );
    this.subscriptions.push(userSubscription);

    // Fetch user data if not already loaded
    const params = {
      userNameFilter: '',
      activeFlag: 1 as 0 | 1,
      sortColumn: 'firstName',
      sortDirection: 'ASC' as 'ASC' | 'DESC',
      limit: 10000,
      offset: 0,
      includedAssociations:
        'PrimaryBuilding, PrimaryPosition, Roles, PrimaryApprovers, SecondaryApprovers, ManagedPrimaryUsers, ManagedSecondaryUsers',
      showAssignedToMe: 0,
      customFlag: 0,
    };

    this.usersService.getUsers(params);
  };

  // Load managers using users API with specific role filtering
  loadManagersFromApproverReference = () => {
    // Get current user's login name
    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        return;
      }

      // Use the existing users API endpoint with high limit to get all users
      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations: 'PrimaryBuilding, PrimaryPosition, Roles',
        limit: 10000, // High limit to get all users
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          // Check different possible response structures
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            // Filter for users who can actually be primary approvers
            this.managers = users.filter((user: any) => {
              return user.Roles?.some((role: any) => {
                const roleName = role.roleName?.toLowerCase() || '';
                const accessLevel = role.accessLevel || '';

                // Only include users with primary approver authority
                return (
                  accessLevel === 'MANAGE_ALL_AUTHORITY' ||
                  accessLevel === 'MANAGE_PRIMARY_AUTHORITY' ||
                  // Include Default Supervisor and Default Administrator roles that have proper authority
                  (roleName === 'default supervisor' &&
                    accessLevel === 'MANAGE_PRIMARY_AUTHORITY') ||
                  (roleName === 'default administrator' &&
                    accessLevel === 'MANAGE_PRIMARY_AUTHORITY')
                );
              });
            });
          }
        },
        error: (error) => {
          console.error('Error getting users:', error);
        },
      });
    });
  };

  // Load secondary managers using users API with specific role filtering
  loadSecondaryManagers = () => {
    // Get current user's login name
    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        return;
      }

      // Use the existing users API endpoint with high limit to get all users
      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations: 'PrimaryBuilding, PrimaryPosition, Roles',
        limit: 10000, // High limit to get all users
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          // Check different possible response structures
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            // Filter for users who can actually be secondary approvers
            this.secondaryManagers = users.filter((user: any) => {
              return user.Roles?.some((role: any) => {
                const roleName = role.roleName?.toLowerCase() || '';
                const accessLevel = role.accessLevel || '';

                // Only include users with secondary approver authority
                return (
                  accessLevel === 'MANAGE_ALL_AUTHORITY' ||
                  accessLevel === 'MANAGE_SECONDARY_AUTHORITY' ||
                  // Include Default Supervisor and Default Administrator roles that have proper authority
                  (roleName === 'default supervisor' &&
                    accessLevel === 'MANAGE_SECONDARY_AUTHORITY') ||
                  (roleName === 'default administrator' &&
                    accessLevel === 'MANAGE_SECONDARY_AUTHORITY')
                );
              });
            });
          }
        },
        error: (error) => {
          console.error('Error getting secondary managers:', error);
        },
      });
    });
  };

  // User search functionality
  onUserSearchChange = (searchText: string) => {
    this.userSearchText = searchText;

    // If search text is empty, show all users
    if (!searchText || searchText.trim() === '') {
      this.filteredUserList = [...this.userList];
    } else {
      // Filter users based on search text
      this.filteredUserList = this.userList.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
  };

  // User selection methods
  toggleUserSelection = (user: any) => {
    const index = this.selectedUsers.findIndex((u) => u.id === user.id);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }
  };

  toggleSelectAllUsers = () => {
    this.selectAllUsers = !this.selectAllUsers;
    if (this.selectAllUsers) {
      this.selectedUsers = [...this.filteredUserList];
    } else {
      this.selectedUsers = [];
    }
  };

  isUserSelected = (user: any) => {
    return this.selectedUsers.some((u) => u.id === user.id);
  };

  // Building selection methods
  onNewBuildingSelected = (building: any) => {
    this.selectedNewBuilding = building;
  };

  onFilterBuildingSelected = (building: any) => {
    this.selectedFilterBuilding = building;
    // Load staff from the selected building
    this.loadStaffFromBuilding(building);
  };

  // Manager selection methods
  onNewManagerSelected = (manager: any) => {
    this.selectedNewManager = manager;
  };

  onFilterManagerSelected = (manager: any) => {
    this.selectedFilterManager = manager;
    // Load staff from the selected manager
    this.loadStaffFromManager(manager);
  };

  // Secondary Manager selection methods
  onNewSecondaryManagerSelected = (manager: any) => {
    this.selectedNewSecondaryManager = manager;
  };

  onFilterSecondaryManagerSelected = (manager: any) => {
    this.selectedFilterSecondaryManager = manager;
    // Load staff from the selected secondary manager
    this.loadStaffFromSecondaryManager(manager);
  };

  // Requirement selection methods
  onRequirementSelected = (requirement: any) => {
    this.selectedRequirement = requirement;
    // Load all staff for requirement assignment
    this.loadAllStaff();
  };

  // User Roles selection methods
  onFilterRoleSelected = (role: any) => {
    this.selectedFilterRole = role;
    // Load staff with the selected role
    this.loadStaffByRole(role);
  };

  onNewRoleSelected = (role: any) => {
    this.selectedNewRole = role;
  };

  loadAllStaff = () => {
    this.userList = []; // Clear existing list
    this.filteredUserList = [];

    // Subscribe to the users data response (same as staff list component)
    const userSubscription = this.usersService.usersData$.subscribe(
      (userData: any) => {
        if (userData?.rows) {
          this.userList = userData.rows;
          this.filteredUserList = [...this.userList];
        }
      }
    );
    this.subscriptions.push(userSubscription);

    // Use the exact same approach as the staff list component
    const params = {
      userNameFilter: '',
      activeFlag: 1 as 0 | 1,
      sortColumn: 'firstName',
      sortDirection: 'ASC' as 'ASC' | 'DESC',
      limit: 1000, // Large limit to get all users at once
      offset: 0,
      includedAssociations:
        'PrimaryBuilding, PrimaryPosition, Roles, PrimaryApprovers, SecondaryApprovers, ManagedPrimaryUsers, ManagedSecondaryUsers',
      showAssignedToMe: 0,
      customFlag: 0,
    };

    this.usersService.getUsers(params);
  };

  // Load staff by role
  loadStaffByRole = (role: any) => {
    this.isLoadingStaff = true;
    this.userList = []; // Clear existing list
    this.filteredUserList = [];
    this.selectedUsers = [];

    this.selectAllUsers = false;
    this.userSearchText = '';

    // Get current user's login name and fetch all users directly
    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        this.isLoadingStaff = false;
        return;
      }

      // Fetch all users directly with high limit to get complete dataset
      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations: 'PrimaryBuilding, PrimaryPosition, Roles',
        limit: 10000, // High limit to get all users
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          // Check different possible response structures
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            // Filter users who have the selected role
            const usersWithRole = users.filter((user: any) => {
              if (user.Roles && Array.isArray(user.Roles)) {
                return user.Roles.some(
                  (userRole: any) => userRole.roleName === role.roleName
                );
              }
              return false;
            });

            this.userList = usersWithRole;
            this.filteredUserList = [...this.userList];
            this.isLoadingStaff = false;
          } else {
            this.isLoadingStaff = false;
          }
        },
        error: (error) => {
          console.error('Error fetching users for role filtering:', error);
          this.isLoadingStaff = false;
        },
      });
    });
  };

  loadStaffFromBuilding = (building: any) => {
    this.userList = []; // Clear existing list
    this.filteredUserList = [];

    // Use the same approach as system settings - get building details with users
    const buildingSubscription = this.buildingsService
      .getBuildingDetails(building.id, 'Users, UserCount')
      .subscribe({
        next: (response: any) => {
          if (response?.data?.Users && Array.isArray(response.data.Users)) {
            // Filter to only active users
            const activeUsers = response.data.Users.filter(
              (user: any) => user.activeFlag === 1
            );

            this.userList = activeUsers;
            this.filteredUserList = [...this.userList];
          } else {
            this.userList = [];
            this.filteredUserList = [];
          }
        },
        error: (error) => {
          console.error('Error fetching building details:', error);
          this.userList = [];
          this.filteredUserList = [];
        },
      });

    this.subscriptions.push(buildingSubscription);
  };

  loadStaffFromManager = (manager: any) => {
    this.isLoadingStaff = true;
    this.userList = []; // Clear existing list
    this.filteredUserList = [];
    this.selectedUsers = [];

    this.selectAllUsers = false;
    this.userSearchText = '';

    // Get current user's login name and fetch all users directly
    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        this.isLoadingStaff = false;
        return;
      }

      // Fetch all users directly with high limit to get complete dataset
      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations: 'PrimaryBuilding, PrimaryPosition, Roles',
        limit: 10000, // High limit to get all users
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          // Check different possible response structures
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            // Get all active users and load detailed info, then filter by manager
            const activeUsers = users.filter(
              (user: any) => user.activeFlag === 1
            );
            this.loadUserDetailsWithPrimaryApproversAndFilter(
              activeUsers,
              manager
            );
          } else {
            this.isLoadingStaff = false;
          }
        },
        error: (error) => {
          console.error('Error fetching users for staff filtering:', error);
          this.isLoadingStaff = false;
        },
      });
    });
  };

  // Load staff from secondary manager (similar to loadStaffFromManager but for secondary approvers)
  loadStaffFromSecondaryManager = (manager: any) => {
    this.isLoadingStaff = true;
    this.userList = []; // Clear existing list
    this.filteredUserList = [];
    this.selectedUsers = [];

    this.selectAllUsers = false;
    this.userSearchText = '';

    // Get current user's login name and fetch all users directly
    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        this.isLoadingStaff = false;
        return;
      }

      // Fetch all users directly with high limit to get complete dataset
      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations:
          'PrimaryBuilding, PrimaryPosition, Roles, SecondaryApprovers',
        limit: 10000, // High limit to get all users
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          // Check different possible response structures
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            // Get all active users and load detailed info, then filter by secondary manager
            const activeUsers = users.filter(
              (user: any) => user.activeFlag === 1
            );
            this.loadUserDetailsWithSecondaryApproversAndFilter(
              activeUsers,
              manager
            );
          } else {
            this.isLoadingStaff = false;
          }
        },
        error: (error) => {
          console.error(
            'Error fetching users for secondary staff filtering:',
            error
          );
          this.isLoadingStaff = false;
        },
      });
    });
  };

  // Load detailed user information including primary approvers for each user and filter by manager
  loadUserDetailsWithPrimaryApproversAndFilter = (
    users: any[],
    manager: any
  ) => {
    if (users.length === 0) {
      this.userList = [];
      this.filteredUserList = [];
      this.isLoadingStaff = false;
      return;
    }

    // OPTIMIZATION: Check if users already have PrimaryApprovers data from the initial load
    const usersWithApprovers = users.filter(
      (user) => user.PrimaryApprovers && Array.isArray(user.PrimaryApprovers)
    );
    const usersWithoutApprovers = users.filter(
      (user) => !user.PrimaryApprovers || !Array.isArray(user.PrimaryApprovers)
    );

    // First, filter users who already have PrimaryApprovers data
    const managedUsersFromExisting = usersWithApprovers.filter((user: any) => {
      return user.PrimaryApprovers.some(
        (approver: any) =>
          approver &&
          approver.id &&
          (approver.id === manager.id || approver.id === Number(manager.id))
      );
    });

    console.log(
      `Manager ${manager.firstName} ${manager.lastName} (ID: ${manager.id})`
    );
    console.log(
      `Users with PrimaryApprovers data: ${usersWithApprovers.length}`
    );
    console.log(
      `Users without PrimaryApprovers data: ${usersWithoutApprovers.length}`
    );
    console.log(
      `Found ${managedUsersFromExisting.length} managed users from existing data`
    );

    // If we have users without PrimaryApprovers data, we need to fetch them
    if (usersWithoutApprovers.length > 0) {
      // Limit the number of HTTP calls to avoid overwhelming the server
      const maxCalls = 50; // Reasonable limit
      const usersToFetch = usersWithoutApprovers.slice(0, maxCalls);

      console.log(
        `Fetching detailed data for ${usersToFetch.length} users (limited from ${usersWithoutApprovers.length} total)`
      );

      const userDetailPromises = usersToFetch.map((user: any) => {
        return this.http
          .get(`${environment.expressUrl}/users/${user.id}`, {
            headers: { 'Content-Type': 'application/vnd.api+json' },
            params: {
              loginName: this.usersService.user?.loginName,
              includedAssociations: 'PrimaryApprovers, SecondaryApprovers',
            },
          })
          .toPromise();
      });

      Promise.all(userDetailPromises)
        .then((responses: any[]) => {
          // Extract user data from responses and combine with original user data
          const detailedUsers = responses.map(
            (response: any, index: number) => {
              if (response?.data) {
                return {
                  ...usersToFetch[index], // Keep original user data
                  ...response.data, // Add detailed data including PrimaryApprovers
                };
              }
              return usersToFetch[index]; // Fallback to original data if detailed fetch failed
            }
          );

          // Filter users who have this manager as their primary approver
          const managedUsersFromFetch = detailedUsers.filter((user: any) => {
            if (user.PrimaryApprovers && Array.isArray(user.PrimaryApprovers)) {
              return user.PrimaryApprovers.some(
                (approver: any) =>
                  approver &&
                  approver.id &&
                  (approver.id === manager.id ||
                    approver.id === Number(manager.id))
              );
            }
            return false;
          });

          // Combine both lists
          const allManagedUsers = [
            ...managedUsersFromExisting,
            ...managedUsersFromFetch,
          ];

          console.log(
            `Found ${managedUsersFromFetch.length} additional managed users from detailed fetch`
          );
          console.log(`Total managed users: ${allManagedUsers.length}`);

          this.userList = allManagedUsers;
          this.filteredUserList = [...this.userList];
          this.isLoadingStaff = false;
        })
        .catch((error: any) => {
          console.error('Error loading detailed user information:', error);
          // Fallback to just the users we found from existing data
          this.userList = managedUsersFromExisting;
          this.filteredUserList = [...this.userList];
          this.isLoadingStaff = false;
        });
    } else {
      // All users already have PrimaryApprovers data, no need for additional HTTP calls
      console.log(
        'All users have PrimaryApprovers data, using existing data only'
      );
      console.log(`Total managed users: ${managedUsersFromExisting.length}`);
      this.userList = managedUsersFromExisting;
      this.filteredUserList = [...this.userList];
      this.isLoadingStaff = false;
    }
  };

  // Load detailed user information including secondary approvers for each user and filter by manager
  loadUserDetailsWithSecondaryApproversAndFilter = (
    users: any[],
    manager: any
  ) => {
    if (users.length === 0) {
      this.userList = [];
      this.filteredUserList = [];
      this.isLoadingStaff = false;
      return;
    }

    // OPTIMIZATION: Check if users already have SecondaryApprovers data from the initial load
    const usersWithApprovers = users.filter(
      (user) =>
        user.SecondaryApprovers && Array.isArray(user.SecondaryApprovers)
    );
    const usersWithoutApprovers = users.filter(
      (user) =>
        !user.SecondaryApprovers || !Array.isArray(user.SecondaryApprovers)
    );

    // First, filter users who already have SecondaryApprovers data
    const managedUsersFromExisting = usersWithApprovers.filter((user: any) => {
      const hasManager = user.SecondaryApprovers.some(
        (approver: any) =>
          approver &&
          approver.id &&
          (approver.id === manager.id || approver.id === Number(manager.id))
      );

      // Debug: Log each user being checked
      if (hasManager) {
        console.log(
          `✓ User ${user.firstName} ${user.lastName} has ${manager.firstName} ${manager.lastName} as secondary manager`
        );
      }

      return hasManager;
    });

    console.log(
      `Secondary Manager ${manager.firstName} ${manager.lastName} (ID: ${manager.id})`
    );
    console.log(
      `Users with SecondaryApprovers data: ${usersWithApprovers.length}`
    );
    console.log(
      `Users without SecondaryApprovers data: ${usersWithoutApprovers.length}`
    );
    console.log(
      `Found ${managedUsersFromExisting.length} managed users from existing data`
    );

    // Debug: Show some examples of users with SecondaryApprovers
    if (usersWithApprovers.length > 0) {
      console.log(
        'Sample users with SecondaryApprovers:',
        usersWithApprovers.slice(0, 3).map((u) => ({
          name: `${u.firstName} ${u.lastName}`,
          secondaryApprovers: u.SecondaryApprovers?.map((a: any) => ({
            id: a.id,
            name: `${a.firstName} ${a.lastName}`,
          })),
        }))
      );
    }

    // If we have users without SecondaryApprovers data, we need to fetch them
    if (usersWithoutApprovers.length > 0) {
      // Limit the number of HTTP calls to avoid overwhelming the server
      const maxCalls = 50; // Reasonable limit
      const usersToFetch = usersWithoutApprovers.slice(0, maxCalls);

      console.log(
        `Fetching detailed data for ${usersToFetch.length} users (limited from ${usersWithoutApprovers.length} total)`
      );

      const userDetailPromises = usersToFetch.map((user: any) => {
        return this.http
          .get(`${environment.expressUrl}/users/${user.id}`, {
            headers: { 'Content-Type': 'application/vnd.api+json' },
          })
          .pipe(
            map((response: any) => {
              // Handle different response structures
              if (response?.data) {
                return response.data;
              } else if (Array.isArray(response)) {
                return response[0];
              }
              return response;
            }),
            catchError((error) => {
              console.error(`Error fetching user ${user.id}:`, error);
              return of(null);
            })
          );
      });

      // Wait for all user detail requests to complete
      forkJoin(userDetailPromises).subscribe(
        (detailedUsers: any[]) => {
          // Filter out null responses and filter by secondary manager
          const validDetailedUsers = detailedUsers.filter(
            (user) => user !== null
          );
          const managedUsersFromFetch = validDetailedUsers.filter(
            (user: any) => {
              if (
                user.SecondaryApprovers &&
                Array.isArray(user.SecondaryApprovers)
              ) {
                return user.SecondaryApprovers.some(
                  (approver: any) =>
                    approver &&
                    approver.id &&
                    (approver.id === manager.id ||
                      approver.id === Number(manager.id))
                );
              }
              return false;
            }
          );

          // Combine both lists
          const allManagedUsers = [
            ...managedUsersFromExisting,
            ...managedUsersFromFetch,
          ];

          console.log(
            `Found ${managedUsersFromFetch.length} additional managed users from detailed fetch`
          );
          console.log(`Total managed users: ${allManagedUsers.length}`);

          // Debug: Show the final list of managed users
          console.log(
            'Final managed users list:',
            allManagedUsers.map((u) => `${u.firstName} ${u.lastName}`)
          );

          this.userList = allManagedUsers;
          this.filteredUserList = [...this.userList];
          this.isLoadingStaff = false;
        },
        (error: any) => {
          console.error('Error loading detailed user information:', error);
          // Fallback to just the users we found from existing data
          this.userList = managedUsersFromExisting;
          this.filteredUserList = [...this.userList];
          this.isLoadingStaff = false;
        }
      );
    } else {
      // All users already have SecondaryApprovers data, no need for additional HTTP calls
      console.log(
        'All users have SecondaryApprovers data, using existing data only'
      );
      console.log(`Total managed users: ${managedUsersFromExisting.length}`);

      // Debug: Show the final list of managed users
      console.log(
        'Final managed users list (no additional fetch):',
        managedUsersFromExisting.map((u) => `${u.firstName} ${u.lastName}`)
      );

      this.userList = managedUsersFromExisting;
      this.filteredUserList = [...this.userList];
      this.isLoadingStaff = false;
    }
  };

  // Load detailed user information including primary approvers for each user
  loadUserDetailsWithPrimaryApprovers = (users: any[]) => {
    if (users.length === 0) {
      this.userList = [];
      this.filteredUserList = [];
      return;
    }

    // Use direct HTTP calls to get user details with primary approvers
    const userDetailPromises = users.map((user: any) => {
      return this.http
        .get(`${environment.expressUrl}/users/${user.id}`, {
          headers: { 'Content-Type': 'application/vnd.api+json' },
          params: {
            loginName: this.usersService.user?.loginName,
            includedAssociations: 'PrimaryApprovers, SecondaryApprovers',
          },
        })
        .toPromise();
    });

    Promise.all(userDetailPromises)
      .then((responses: any[]) => {
        // Extract user data from responses and combine with original user data
        const detailedUsers = responses.map((response: any, index: number) => {
          if (response?.data) {
            return {
              ...users[index], // Keep original user data
              ...response.data, // Add detailed data including PrimaryApprovers
            };
          }
          return users[index]; // Fallback to original data if detailed fetch failed
        });

        this.userList = detailedUsers;
        this.filteredUserList = [...this.userList];
      })
      .catch((error: any) => {
        console.error('Error loading detailed user information:', error);
        // Fallback to original user data without primary approvers
        this.userList = users;
        this.filteredUserList = [...this.userList];
      });
  };

  resetBuildingReassignment = () => {
    this.selectedNewBuilding = null;
    this.selectedFilterBuilding = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.buildings = [];
  };

  resetRequirementReassignment = () => {
    this.selectedRequirement = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.requirements = [];
  };

  resetPrimaryManagerReassignment = () => {
    this.selectedNewManager = null;
    this.selectedFilterManager = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.managers = [];
  };

  resetSecondaryManagerReassignment = () => {
    this.selectedNewSecondaryManager = null;
    this.selectedFilterSecondaryManager = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.secondaryManagers = [];
  };

  resetUserRolesReassignment = () => {
    this.selectedFilterRole = null;
    this.selectedNewRole = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.availableRoles = [];
  };

  // Assign building to selected users
  assignBuildingToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedNewBuilding) {
      this.toastService.setToast({
        text: 'Please select both users and a building to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    const userIds = this.selectedUsers.map((user: any) => user.id);

    if (this.assignmentType === 'move') {
      // Move: Use batch reassign with maintainBuildingAssignment = false
      const currentBuildingId = this.selectedFilterBuilding?.id || 0;

      // Create a custom batch reassign call with maintainBuildingAssignment = false
      const moveRequest = this.http.put(
        `${environment.expressUrl}/maintenance/buildings/reassign-users`,
        {
          loginName: this.usersService.user.loginName,
          maintainBuildingAssignment: false, // Remove from current building
          overwritePrimaryBuilding: true, // Set new building as primary
          userIds: userIds.map((id: any) => parseFloat(id)),
          curBuildingId: parseFloat(currentBuildingId),
          newBuildingId: parseFloat(this.selectedNewBuilding.id),
        },
        { headers: { 'Content-Type': 'application/vnd.api+json' } }
      );

      moveRequest.subscribe({
        next: (response: any) => {
          this.toastService.setToast({
            text: `Successfully moved ${this.selectedUsers.length} staff member(s) to "${this.selectedNewBuilding.buildingName}"`,
            type: 'success',
          });

          this.clearSelectionsAndRefresh();
        },
        error: (error: any) => {
          console.error('Error moving users to building:', error);
          this.toastService.setToast({
            text: 'Error moving staff to building. Please try again.',
            type: 'error',
          });
        },
      });
    } else {
      // Add: Use batch reassign with maintainBuildingAssignment = true
      const currentBuildingId = this.selectedFilterBuilding?.id || 0;

      // Create a custom batch reassign call with maintainBuildingAssignment = true
      const addRequest = this.http.put(
        `${environment.expressUrl}/maintenance/buildings/reassign-users`,
        {
          loginName: this.usersService.user.loginName,
          maintainBuildingAssignment: true, // Keep current building assignment
          overwritePrimaryBuilding: false, // Don't change primary building
          userIds: userIds.map((id: any) => parseFloat(id)),
          curBuildingId: parseFloat(currentBuildingId),
          newBuildingId: parseFloat(this.selectedNewBuilding.id),
        },
        { headers: { 'Content-Type': 'application/vnd.api+json' } }
      );

      addRequest.subscribe({
        next: (response: any) => {
          this.toastService.setToast({
            text: `Successfully added ${this.selectedUsers.length} staff member(s) to "${this.selectedNewBuilding.buildingName}"`,
            type: 'success',
          });

          this.clearSelectionsAndRefresh();
        },
        error: (error: any) => {
          console.error('Error adding users to building:', error);
          this.toastService.setToast({
            text: 'Error adding staff to building. Please try again.',
            type: 'error',
          });
        },
      });
    }
  };

  // Assign requirements to selected users using the working individual user approach
  assignRequirementsToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedRequirement) {
      this.toastService.setToast({
        text: 'Please select both users and a requirement to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    // Set processing state
    this.isProcessing = true;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = Math.max(30, this.selectedUsers.length * 0.5); // Estimate 0.5 seconds per user, minimum 30 seconds
    this.progressMessage = `Processing bulk assignment for ${this.selectedUsers.length} users...`;

    // Scroll to progress indicator after a short delay to ensure it's rendered
    setTimeout(() => {
      const progressElement = document.getElementById('progress-indicator');
      if (progressElement) {
        progressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // Show initial progress toast
    this.toastService.setToast({
      text: `🔄 Starting bulk assignment for ${this.selectedUsers.length} users... Estimated time: ${this.estimatedTimeRemaining} seconds. Please do not navigate away.`,
      type: 'info',
      icon: true,
    });

    // Simulate progress bar with realistic timing
    const progressInterval = setInterval(() => {
      if (this.isProcessing) {
        // Gradually increase progress percentage
        this.progressPercentage = Math.min(
          95,
          this.progressPercentage + Math.random() * 10
        );
        this.estimatedTimeRemaining = Math.max(
          0,
          this.estimatedTimeRemaining - 2
        );

        this.progressMessage = `Processing bulk assignment for ${
          this.selectedUsers.length
        } users... ${Math.round(this.progressPercentage)}% complete`;
      }
    }, 2000); // Update every 2 seconds

    // Prevent navigation away during bulk operation
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.isProcessing) {
        event.preventDefault();
        event.returnValue =
          'Bulk assignment in progress. Are you sure you want to leave?';
        return 'Bulk assignment in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    // Use the new bulk endpoint
    const userIds = this.selectedUsers.map((user) => Number(user.id));
    const requirementData = {
      requirementId: Number(
        this.selectedRequirement.requirementId || this.selectedRequirement.id
      ),
      requiredValue: Number(
        this.selectedRequirement.Requirement?.trackingValue ||
          this.selectedRequirement.trackingValue
      ),
    };

    // Add a timeout to detect if the bulk endpoint is hanging
    const bulkRequest = this.usersService.bulkAssignRequirements(
      userIds,
      requirementData
    );

    // Set a timeout to detect if the request is hanging
    const timeout = setTimeout(() => {
      console.error(
        'Bulk request timed out after 10 seconds - this suggests the endpoint is not working'
      );
    }, 10000);

    bulkRequest.subscribe({
      next: (response: any) => {
        clearTimeout(timeout);

        // The bulk endpoint has accepted the request, now we need to wait for all assignments to complete
        this.waitForAssignmentsToComplete(
          userIds,
          requirementData,
          progressInterval,
          beforeUnloadHandler
        );
      },
      error: (error: any) => {
        clearTimeout(timeout);
        clearInterval(progressInterval);
        window.removeEventListener('beforeunload', beforeUnloadHandler);
        this.isProcessing = false;
        this.progressMessage = '';

        console.error('Error in bulk assignment:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        console.error('Full error object:', JSON.stringify(error, null, 2));

        this.toastService.setToast({
          text: `❌ Failed to assign requirement "${
            this.selectedRequirement.requirement ||
            this.selectedRequirement.name
          }" to users. Please try again.`,
          type: 'error',
          icon: true,
        });

        this.resetRequirementReassignment();
        this.goBack();
      },
    });
  };

  /**
   * Wait for all requirement assignments to complete by waiting a reasonable amount of time
   */
  waitForAssignmentsToComplete = (
    userIds: number[],
    requirementData: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) => {
    // Calculate wait time based on number of users (minimum 5 seconds, maximum 30 seconds)
    const baseWaitTime = 5000; // 5 seconds base
    const perUserWaitTime = Math.min(1000, Math.max(200, userIds.length * 50)); // 50ms per user, max 1 second
    const totalWaitTime = Math.min(30000, baseWaitTime + perUserWaitTime); // Max 30 seconds

    // Update progress message to show we're waiting
    this.progressMessage = `Processing ${userIds.length} user assignments... Please wait...`;

    // Wait for the calculated time, then show success
    setTimeout(() => {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);

      // Complete the progress bar
      this.progressPercentage = 100;
      this.progressMessage = 'Bulk assignment completed successfully!';

      // Small delay to show 100% completion
      setTimeout(() => {
        this.isProcessing = false;
        this.progressMessage = '';
        this.progressPercentage = 0;
      }, 1000);

      this.toastService.setToast({
        text: `✅ Successfully assigned requirement "${
          this.selectedRequirement.requirement || this.selectedRequirement.name
        }" to all ${this.selectedUsers.length} users!`,
        type: 'success',
        icon: true,
      });

      // Clear any cached user data to ensure fresh data is loaded
      this.usersService.clearSelectedUser();

      this.resetRequirementReassignment();
      this.goBack();
    }, totalWaitTime);
  };

  // Utility function to batch user updates in groups of 10
  private async batchUserUpdates(
    userIds: number[],
    requestBody: any,
    isRemoveOperation = false,
    batchSize = 10
  ): Promise<any[]> {
    const results: any[] = [];
    const batches = this.chunkArray(userIds, batchSize);
    let totalUsersProcessed = 0;

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      const batchPromises = batch.map((userId: number) => {
        return this.usersService.updateUser(requestBody, userId).toPromise();
      });

      try {
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);

        // Update total users processed
        totalUsersProcessed += batch.length;

        // Update progress based on actual users processed, not batches
        const progressPercent = Math.round(
          (totalUsersProcessed / userIds.length) * 90
        );

        // Ensure progress never goes backwards
        this.progressPercentage = Math.max(
          this.progressPercentage,
          progressPercent
        );
        this.progressMessage = `Processing ${totalUsersProcessed} of ${userIds.length} users... ${this.progressPercentage}% complete`;

        // Small delay between batches to prevent overwhelming the server
        if (i < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`Error in batch ${i + 1}:`, error);
        console.error('Full error response:', error.error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        throw error;
      }
    }

    return results;
  }

  // Utility function to chunk array into smaller arrays
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  // Assign manager to selected users
  assignManagerToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedNewManager) {
      this.toastService.setToast({
        text: 'Please select both users and a manager to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    // Set processing state
    this.isProcessing = true;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = Math.max(5, this.selectedUsers.length * 0.5); // Estimate 0.5 seconds per user, minimum 5 seconds
    this.progressMessage = `Processing bulk manager assignment for ${this.selectedUsers.length} users...`;

    // Scroll to progress indicator after a short delay to ensure it's rendered
    setTimeout(() => {
      const progressElement = document.getElementById('progress-indicator');
      if (progressElement) {
        progressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // Simulate progress bar with more realistic timing
    const progressInterval = setInterval(() => {
      if (this.isProcessing) {
        // Gradually increase progress percentage
        this.progressPercentage = Math.min(
          90,
          this.progressPercentage + Math.random() * 15
        );
        this.estimatedTimeRemaining = Math.max(
          0,
          this.estimatedTimeRemaining - 1
        );

        this.progressMessage = `Processing bulk manager assignment for ${
          this.selectedUsers.length
        } users... ${Math.round(this.progressPercentage)}% complete`;
      }
    }, 500); // Update every 0.5 seconds for smoother progress

    // Prevent navigation away during bulk operation
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.isProcessing) {
        event.preventDefault();
        event.returnValue =
          'Bulk manager assignment in progress. Are you sure you want to leave?';
        return 'Bulk manager assignment in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    const userIds = [
      ...new Set(this.selectedUsers.map((user: any) => Number(user.id))),
    ];
    const managerId = Number(this.selectedNewManager.id);

    // Prepare the request body based on assignment type
    const requestBody: any = { loginName: this.usersService.user?.loginName };

    if (this.managerAssignmentType === 'move') {
      // Move: Remove from ALL current primary approvers and add to new manager
      // Get all current primary approver IDs from the selected users
      const allCurrentPrimaryApproverIds = new Set<number>();

      this.selectedUsers.forEach((user: any) => {
        if (user.PrimaryApprovers && Array.isArray(user.PrimaryApprovers)) {
          user.PrimaryApprovers.forEach((approver: any) => {
            if (approver && approver.id) {
              allCurrentPrimaryApproverIds.add(Number(approver.id));
            }
          });
        }
      });

      // Remove from all current primary approvers EXCEPT the new manager
      const approversToRemove = Array.from(allCurrentPrimaryApproverIds).filter(
        (approverId: number) => approverId !== managerId
      );

      if (approversToRemove.length > 0) {
        requestBody.removePrimaryApproverIds = approversToRemove;
      }

      // Add to new manager
      requestBody.addPrimaryApproverIds = [managerId];

      // Check if all users are already assigned to the target manager
      if (
        approversToRemove.length === 0 &&
        allCurrentPrimaryApproverIds.has(managerId)
      ) {
        this.toastService.setToast({
          text: `All selected users are already assigned to "${this.selectedNewManager.firstName} ${this.selectedNewManager.lastName}". No changes were made.`,
          type: 'info',
          icon: true,
        });
        this.isProcessing = false;
        this.progressPercentage = 0;
        this.progressMessage = '';
        return;
      }
    } else {
      // Add: Just add to new manager (keep existing managers)
      requestBody.addPrimaryApproverIds = [managerId];
    }

    // Use batching to update primary approvers
    this.processBatchedManagerUpdates(
      userIds,
      requestBody,
      progressInterval,
      beforeUnloadHandler
    );
  };

  // Process batched manager updates
  private async processBatchedManagerUpdates(
    userIds: number[],
    requestBody: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) {
    try {
      if (
        this.managerAssignmentType === 'move' &&
        requestBody.removePrimaryApproverIds?.length > 0
      ) {
        // Step 1: Remove all current primary approvers in batches (0-45%)
        const removeRequestBody = {
          loginName: this.usersService.user?.loginName,
          removePrimaryApproverIds: requestBody.removePrimaryApproverIds,
        };

        this.progressMessage = `Removing current managers from ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, removeRequestBody, true);

        // Step 2: Add new manager in batches (45-90%)
        const addRequestBody = {
          loginName: this.usersService.user?.loginName,
          addPrimaryApproverIds: requestBody.addPrimaryApproverIds,
        };

        // Ensure we don't reset progress when starting step 2
        const currentProgress = this.progressPercentage;
        this.progressMessage = `Adding new manager to ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, addRequestBody, false);
      } else {
        // For add operations or when no removal is needed, use single batched call
        await this.batchUserUpdates(userIds, requestBody, false);
      }

      // All batches completed successfully
      this.waitForManagerAssignmentsToComplete(
        userIds,
        requestBody,
        progressInterval,
        beforeUnloadHandler
      );
    } catch (error: any) {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;
      this.progressMessage = '';

      console.error('Error in batched manager assignment:', error);
      console.error('Error status:', error.status);
      console.error('Error message:', error.message);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      this.toastService.setToast({
        text: `❌ Failed to assign manager "${this.selectedNewManager.firstName} ${this.selectedNewManager.lastName}" to users. Please try again.`,
        type: 'error',
        icon: true,
      });

      this.resetPrimaryManagerReassignment();
      this.goBack();
    }
  }

  // Assign secondary manager to selected users (similar to assignManagerToUsers but for secondary approvers)
  assignSecondaryManagerToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedNewSecondaryManager) {
      this.toastService.setToast({
        text: 'Please select both users and a secondary manager to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    // Set processing state
    this.isProcessing = true;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = Math.max(5, this.selectedUsers.length * 0.5); // Estimate 0.5 seconds per user, minimum 5 seconds
    this.progressMessage = `Processing bulk secondary manager assignment for ${this.selectedUsers.length} users...`;

    // Scroll to progress indicator after a short delay to ensure it's rendered
    setTimeout(() => {
      const progressElement = document.getElementById('progress-indicator');
      if (progressElement) {
        progressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // Simulate progress bar with more realistic timing
    const progressInterval = setInterval(() => {
      if (this.isProcessing) {
        // Gradually increase progress percentage
        this.progressPercentage = Math.min(
          90,
          this.progressPercentage + Math.random() * 15
        );
        this.estimatedTimeRemaining = Math.max(
          0,
          this.estimatedTimeRemaining - 1
        );

        this.progressMessage = `Processing bulk secondary manager assignment for ${
          this.selectedUsers.length
        } users... ${Math.round(this.progressPercentage)}% complete`;
      }
    }, 500); // Update every 0.5 seconds for smoother progress

    // Prevent navigation away during bulk operation
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.isProcessing) {
        event.preventDefault();
        event.returnValue =
          'Bulk secondary manager assignment in progress. Are you sure you want to leave?';
        return 'Bulk secondary manager assignment in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    const userIds = [
      ...new Set(this.selectedUsers.map((user: any) => Number(user.id))),
    ];
    const managerId = Number(this.selectedNewSecondaryManager.id);

    // Prepare the request body based on assignment type
    const requestBody: any = { loginName: this.usersService.user?.loginName };

    if (this.managerAssignmentType === 'move') {
      // Move: Remove from ALL current secondary approvers and add to new manager
      // Get all current secondary approver IDs from the selected users
      const allCurrentSecondaryApproverIds = new Set<number>();

      this.selectedUsers.forEach((user: any) => {
        if (user.SecondaryApprovers && Array.isArray(user.SecondaryApprovers)) {
          user.SecondaryApprovers.forEach((approver: any) => {
            if (approver && approver.id) {
              allCurrentSecondaryApproverIds.add(Number(approver.id));
            }
          });
        }
      });

      // Remove from all current secondary approvers EXCEPT the new manager
      const approversToRemove = Array.from(
        allCurrentSecondaryApproverIds
      ).filter((approverId: number) => approverId !== managerId);

      if (approversToRemove.length > 0) {
        requestBody.removeSecondaryApproverIds = approversToRemove;
      }

      // Add to new manager
      requestBody.addSecondaryApproverIds = [managerId];

      // Check if all users are already assigned to the target manager
      if (
        approversToRemove.length === 0 &&
        allCurrentSecondaryApproverIds.has(managerId)
      ) {
        this.toastService.setToast({
          text: `All selected users are already assigned to "${this.selectedNewSecondaryManager.firstName} ${this.selectedNewSecondaryManager.lastName}". No changes were made.`,
          type: 'info',
          icon: true,
        });
        this.isProcessing = false;
        this.progressPercentage = 0;
        this.progressMessage = '';
        return;
      }
    } else {
      // Add: Just add to new manager (keep existing managers)
      requestBody.addSecondaryApproverIds = [managerId];
    }

    // Use batching to update secondary approvers
    this.processBatchedSecondaryManagerUpdates(
      userIds,
      requestBody,
      progressInterval,
      beforeUnloadHandler
    );
  };

  // Process batched secondary manager updates
  private async processBatchedSecondaryManagerUpdates(
    userIds: number[],
    requestBody: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) {
    try {
      if (
        this.managerAssignmentType === 'move' &&
        requestBody.removeSecondaryApproverIds?.length > 0
      ) {
        // Step 1: Remove all current secondary approvers in batches (0-45%)
        const removeRequestBody = {
          loginName: this.usersService.user?.loginName,
          removeSecondaryApproverIds: requestBody.removeSecondaryApproverIds,
        };

        this.progressMessage = `Removing current secondary managers from ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, removeRequestBody, true);

        // Step 2: Add new secondary manager in batches (45-90%)
        const addRequestBody = {
          loginName: this.usersService.user?.loginName,
          addSecondaryApproverIds: requestBody.addSecondaryApproverIds,
        };

        // Ensure we don't reset progress when starting step 2
        const currentProgress = this.progressPercentage;
        this.progressMessage = `Adding new secondary manager to ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, addRequestBody, false);
      } else {
        // For add operations or when no removal is needed, use single batched call
        await this.batchUserUpdates(userIds, requestBody, false);
      }

      // All batches completed successfully
      this.waitForSecondaryManagerAssignmentsToComplete(
        userIds,
        requestBody,
        progressInterval,
        beforeUnloadHandler
      );
    } catch (error: any) {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;
      this.progressMessage = '';

      console.error('Error in batched secondary manager assignment:', error);
      console.error('Error status:', error.status);
      console.error('Error message:', error.message);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      this.toastService.setToast({
        text: `❌ Failed to assign secondary manager "${this.selectedNewSecondaryManager.firstName} ${this.selectedNewSecondaryManager.lastName}" to users. Please try again.`,
        type: 'error',
        icon: true,
      });

      this.resetSecondaryManagerReassignment();
      this.goBack();
    }
  }

  // Assign role to selected users (move only - users can only have one role)
  assignRoleToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedNewRole) {
      this.toastService.setToast({
        text: 'Please select both users and a new role to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    // Check permissions before attempting role assignment
    const currentUser = this.usersService.user;
    const hasUserEdit = currentUser?.Permissions?.USER_EDIT === 1;
    const isManagerOfUsers = this.selectedUsers.every((user: any) => {
      const isPrimaryManager = currentUser?.ManagedPrimaryUsers?.some(
        (managed: any) => managed.id === user.id
      );
      const isSecondaryManager = currentUser?.ManagedSecondaryUsers?.some(
        (managed: any) => managed.id === user.id
      );
      return isPrimaryManager || isSecondaryManager;
    });

    if (!hasUserEdit && !isManagerOfUsers) {
      this.toastService.setToast({
        text: 'You do not have permission to modify user roles. You need USER_EDIT permission or must be a manager of the selected users.',
        type: 'error',
        icon: true,
      });
      return;
    }

    // Set processing state
    this.isProcessing = true;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = Math.max(5, this.selectedUsers.length * 0.5);
    this.progressMessage = `Processing bulk role assignment for ${this.selectedUsers.length} users...`;

    // Scroll to progress indicator
    setTimeout(() => {
      const progressElement = document.getElementById('progress-indicator');
      if (progressElement) {
        progressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    // For individual role updates, we'll calculate progress manually
    // so we don't need the random progress interval
    const progressInterval = null;

    // Prevent navigation away during bulk operation
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.isProcessing) {
        event.preventDefault();
        event.returnValue =
          'Bulk role assignment in progress. Are you sure you want to leave?';
        return 'Bulk role assignment in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    const userIds = [
      ...new Set(this.selectedUsers.map((user: any) => Number(user.id))),
    ];

    // For role assignment, we need to:
    // 1. Get all current role IDs for each user
    // 2. Remove all current roles
    // 3. Add the new role

    // First, get all current role IDs from selected users
    const allCurrentRoleIds = new Set<number>();
    this.selectedUsers.forEach((user: any) => {
      if (user.Roles && Array.isArray(user.Roles)) {
        user.Roles.forEach((role: any) => {
          if (role && role.id) {
            allCurrentRoleIds.add(Number(role.id));
          }
        });
      }
    });

    // Find the new role ID from available roles
    const newRoleId = this.availableRoles.find(
      (role: any) => role.roleName === this.selectedNewRole.roleName
    )?.id;

    if (!newRoleId) {
      this.toastService.setToast({
        text: 'Error: Could not find the selected role ID. Please try again.',
        type: 'error',
        icon: true,
      });
      this.isProcessing = false;
      this.progressMessage = '';
      return;
    }

    // Prepare the request body for role assignment
    // Use the same format as manager assignments but for roles
    const requestBody: any = {
      loginName: this.usersService.user?.loginName,
      addRoleIds: [newRoleId],
    };

    // Only include removeRoleIds if there are roles to remove
    if (allCurrentRoleIds.size > 0) {
      requestBody.removeRoleIds = Array.from(allCurrentRoleIds);
    }

    // Debug logging
    console.log('Role assignment request body:', requestBody);
    console.log('Current role IDs to replace:', Array.from(allCurrentRoleIds));
    console.log('New role ID to assign:', newRoleId);
    console.log('User IDs to update:', userIds);
    console.log(
      'Login name (added by service):',
      this.usersService.user?.loginName
    );
    console.log(
      'Current user permissions:',
      this.usersService.user?.Permissions
    );
    console.log('Current user managed users:', {
      primary: this.usersService.user?.ManagedPrimaryUsers,
      secondary: this.usersService.user?.ManagedSecondaryUsers,
    });

    // Use individual role updates since each user may have different current roles
    this.processIndividualRoleUpdates(
      userIds,
      newRoleId,
      progressInterval,
      beforeUnloadHandler
    );
  };

  // Process individual role updates using User class approach
  private async processIndividualRoleUpdates(
    userIds: number[],
    newRoleId: number,
    progressInterval: any,
    beforeUnloadHandler: any
  ) {
    try {
      this.progressMessage = `Assigning new role to ${userIds.length} users...`;

      // Process users one by one using the User class approach
      for (let i = 0; i < userIds.length; i++) {
        const userId = userIds[i];

        // Fetch current user data first to get previous role IDs
        let currentUserData: any = null;

        // Clear any previous user data and fetch new data
        this.usersService.clearSelectedUser();
        this.usersService.getUserById(userId, {
          includedAssociations: 'Roles',
        });

        // Wait for the user data to be available
        await new Promise((resolve, reject) => {
          const subscription = this.usersService.selectedUserData$.subscribe(
            (userData: any) => {
              if (userData) {
                currentUserData = userData;
                subscription.unsubscribe();
                resolve(userData);
              }
            }
          );

          // Timeout after 5 seconds
          setTimeout(() => {
            subscription.unsubscribe();
            reject(new Error(`Timeout fetching user data for user ${userId}`));
          }, 5000);
        });

        if (!currentUserData) {
          console.error(`Failed to fetch user data for user ${userId}`);
          continue;
        }

        // Create a User instance with the current user data
        const user = new UserGenerator(currentUserData);
        user.id = userId;

        // Use the updateValue method like the working example
        user.updateValue('roleIds', newRoleId);

        // Get the request body from the user
        const requestBody = user.generateRequest();

        console.log(`Updating user ${userId} with request body:`, requestBody);

        // Make the individual update
        await this.usersService.updateUser(requestBody, userId).toPromise();

        // Update progress
        const progress = Math.round(((i + 1) / userIds.length) * 90);
        this.progressPercentage = progress;
        this.progressMessage = `Updated ${i + 1} of ${userIds.length} users...`;
      }

      // Final progress update
      this.progressPercentage = 100;
      this.progressMessage = `Role assignment complete for ${userIds.length} users!`;
      this.estimatedTimeRemaining = 0;

      if (progressInterval) {
        clearInterval(progressInterval);
      }
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;

      this.toastService.setToast({
        text: `✅ Successfully assigned role "${this.selectedNewRole.roleName}" to ${userIds.length} users.`,
        type: 'success',
        icon: true,
      });

      this.resetUserRolesReassignment();
      this.goBack();
    } catch (error: any) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;
      this.progressMessage = '';

      console.error('Error in individual role updates:', error);
      console.error('Full error response:', error.error);
      console.error('Error status:', error.status);
      console.error('Error message:', error.message);

      // Check if it's a 403 error about management assignments
      if (
        error.status === 403 &&
        error.error?.message?.includes('Management assignments')
      ) {
        this.toastService.setToast({
          text: `❌ Cannot assign roles to users with management assignments. Users with primary or secondary management assignments must have those assignments removed first before changing their role.`,
          type: 'error',
          icon: true,
          dismissible: true, // Persistent - user must manually close
        });
      } else {
        this.toastService.setToast({
          text: `❌ Failed to assign role "${this.selectedNewRole.roleName}" to users. Please try again.`,
          type: 'error',
          icon: true,
          dismissible: true, // Persistent - user must manually close
        });
      }

      this.resetUserRolesReassignment();
      this.goBack();
    }
  }

  // Process batched role updates
  private async processBatchedRoleUpdates(
    userIds: number[],
    requestBody: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) {
    try {
      if (requestBody.removeRoleIds?.length > 0) {
        // Step 1: Remove all current roles in batches (0-45%)
        const removeRequestBody = {
          loginName: this.usersService.user?.loginName,
          removeRoleIds: requestBody.removeRoleIds,
        };

        this.progressMessage = `Removing current roles from ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, removeRequestBody, true);

        // Step 2: Add new role in batches (45-90%)
        const addRequestBody = {
          loginName: this.usersService.user?.loginName,
          addRoleIds: requestBody.addRoleIds,
        };

        // Ensure we don't reset progress when starting step 2
        const currentProgress = this.progressPercentage;
        this.progressMessage = `Adding new role to ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, addRequestBody, false);
      } else {
        // For cases where there are no roles to remove, just add the new role
        const addRequestBody = {
          loginName: this.usersService.user?.loginName,
          addRoleIds: requestBody.addRoleIds,
        };
        this.progressMessage = `Assigning new role to ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, addRequestBody, false);
      }

      // All batches completed successfully
      this.waitForRoleAssignmentsToComplete(
        userIds,
        requestBody,
        progressInterval,
        beforeUnloadHandler
      );
    } catch (error: any) {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;
      this.progressMessage = '';

      console.error('Error in batched role updates:', error);
      console.error('Full error response:', error.error);
      console.error('Error status:', error.status);
      console.error('Error message:', error.message);

      // Check if it's a 403 error about management assignments
      if (
        error.status === 403 &&
        error.error?.message?.includes('Management assignments')
      ) {
        this.toastService.setToast({
          text: `❌ Cannot assign roles to users with management assignments. Users with primary or secondary management assignments must have those assignments removed first before changing their role.`,
          type: 'error',
          icon: true,
          dismissible: true, // Persistent - user must manually close
        });
      } else {
        this.toastService.setToast({
          text: `❌ Failed to assign role "${this.selectedNewRole.roleName}" to users. Please try again.`,
          type: 'error',
          icon: true,
          dismissible: true, // Persistent - user must manually close
        });
      }

      this.resetUserRolesReassignment();
      this.goBack();
    }
  }

  /**
   * Wait for all role assignments to complete by waiting a reasonable amount of time
   */
  waitForRoleAssignmentsToComplete = (
    userIds: number[],
    requestBody: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) => {
    // Calculate wait time based on number of users (minimum 2 seconds, maximum 10 seconds)
    const baseWaitTime = 2000; // 2 seconds base
    const perUserWaitTime = Math.min(1000, Math.max(200, userIds.length * 50)); // 50ms per user, max 1 second
    const totalWaitTime = Math.min(10000, baseWaitTime + perUserWaitTime); // Max 10 seconds

    // Update progress message to show we're waiting
    this.progressMessage = `Processing ${userIds.length} user role assignments... Please wait...`;

    // Wait for the calculated time, then show success
    setTimeout(() => {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);

      // Complete the progress bar
      this.progressPercentage = 100;
      this.progressMessage = 'Bulk role assignment completed successfully!';

      // Small delay to show 100% completion
      setTimeout(() => {
        this.isProcessing = false;
        this.progressMessage = '';
        this.progressPercentage = 0;
      }, 1000);

      this.toastService.setToast({
        text: `✅ Successfully assigned role "${this.selectedNewRole.roleName}" to all ${this.selectedUsers.length} users!`,
        type: 'success',
        icon: true,
      });

      // Clear any cached user data to ensure fresh data is loaded
      this.usersService.clearSelectedUser();

      // Refresh the user data to reflect the role changes
      this.refreshUserDataAfterReassignment();

      // Clear selections and go back to role selection to show updated state
      this.selectedUsers = [];
      this.selectAllUsers = false;
      this.selectedFilterRole = null;
      this.userList = [];
      this.filteredUserList = [];
      this.userSearchText = '';

      // Simulate clicking back to return to reassignment types selection
      this.goBack();
    }, totalWaitTime);
  };

  /**
   * Wait for all manager assignments to complete by waiting a reasonable amount of time
   */
  waitForManagerAssignmentsToComplete = (
    userIds: number[],
    requestBody: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) => {
    // Calculate wait time based on number of users (minimum 2 seconds, maximum 10 seconds)
    // Reduced wait time since API calls are typically much faster
    const baseWaitTime = 2000; // 2 seconds base
    const perUserWaitTime = Math.min(1000, Math.max(200, userIds.length * 50)); // 50ms per user, max 1 second
    const totalWaitTime = Math.min(10000, baseWaitTime + perUserWaitTime); // Max 10 seconds

    // Update progress message to show we're waiting
    this.progressMessage = `Processing ${userIds.length} user manager assignments... Please wait...`;

    // Wait for the calculated time, then show success
    setTimeout(() => {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);

      // Complete the progress bar
      this.progressPercentage = 100;
      this.progressMessage = 'Bulk manager assignment completed successfully!';

      // Small delay to show 100% completion
      setTimeout(() => {
        this.isProcessing = false;
        this.progressMessage = '';
        this.progressPercentage = 0;
      }, 1000);

      this.toastService.setToast({
        text: `✅ Successfully assigned manager "${this.selectedNewManager.firstName} ${this.selectedNewManager.lastName}" to all ${this.selectedUsers.length} users!`,
        type: 'success',
        icon: true,
      });

      // Clear any cached user data to ensure fresh data is loaded
      this.usersService.clearSelectedUser();

      // Refresh the user data to reflect the manager changes
      this.refreshUserDataAfterReassignment();

      // Clear selections and go back to manager selection to show updated state
      this.selectedUsers = [];
      this.selectAllUsers = false;
      this.selectedFilterManager = null;
      this.userList = [];
      this.filteredUserList = [];
      this.userSearchText = '';

      // Simulate clicking back to return to reassignment types selection
      this.goBack();
    }, totalWaitTime);
  };

  /**
   * Wait for all secondary manager assignments to complete by waiting a reasonable amount of time
   */
  waitForSecondaryManagerAssignmentsToComplete = (
    userIds: number[],
    requestBody: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) => {
    // Calculate wait time based on number of users (minimum 2 seconds, maximum 10 seconds)
    // Reduced wait time since API calls are typically much faster
    const baseWaitTime = 2000; // 2 seconds base
    const perUserWaitTime = Math.min(1000, Math.max(200, userIds.length * 50)); // 50ms per user, max 1 second
    const totalWaitTime = Math.min(10000, baseWaitTime + perUserWaitTime); // Max 10 seconds

    // Update progress message to show we're waiting
    this.progressMessage = `Processing ${userIds.length} user secondary manager assignments... Please wait...`;

    // Wait for the calculated time, then show success
    setTimeout(() => {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);

      // Complete the progress bar
      this.progressPercentage = 100;
      this.progressMessage =
        'Bulk secondary manager assignment completed successfully!';

      // Small delay to show 100% completion
      setTimeout(() => {
        this.isProcessing = false;
        this.progressMessage = '';
        this.progressPercentage = 0;
      }, 1000);

      this.toastService.setToast({
        text: `✅ Successfully assigned secondary manager "${this.selectedNewSecondaryManager.firstName} ${this.selectedNewSecondaryManager.lastName}" to all ${this.selectedUsers.length} users!`,
        type: 'success',
        icon: true,
      });

      // Clear any cached user data to ensure fresh data is loaded
      this.usersService.clearSelectedUser();

      // Refresh the user data to reflect the secondary manager changes
      this.refreshUserDataAfterReassignment();

      // Clear selections and go back to secondary manager selection to show updated state
      this.selectedUsers = [];
      this.selectAllUsers = false;
      this.selectedFilterSecondaryManager = null;
      this.userList = [];
      this.filteredUserList = [];
      this.userSearchText = '';

      // Simulate clicking back to return to reassignment types selection
      this.goBack();
    }, totalWaitTime);
  };

  clearSelectionsAndRefresh = () => {
    this.selectedUsers = [];
    this.selectAllUsers = false;

    // Refresh the staff list to show updated assignments
    if (this.selectedFilterBuilding) {
      this.loadStaffFromBuilding(this.selectedFilterBuilding);
    } else if (this.selectedFilterManager) {
      this.loadStaffFromManager(this.selectedFilterManager);
    } else if (this.selectedFilterSecondaryManager) {
      this.loadStaffFromSecondaryManager(this.selectedFilterSecondaryManager);
    } else if (this.selectedRequirement) {
      this.loadAllStaff();
    }
  };

  /**
   * Refresh user data after a successful manager reassignment to ensure
   * the UI reflects the updated manager assignments immediately
   */
  refreshUserDataAfterReassignment = () => {
    // Force refresh the users data from the server
    const params = {
      userNameFilter: '',
      activeFlag: 1 as 0 | 1,
      sortColumn: 'firstName',
      sortDirection: 'ASC' as 'ASC' | 'DESC',
      limit: 10000, // Increased limit to get all 4,037+ users
      offset: 0,
      includedAssociations:
        'PrimaryBuilding, PrimaryPosition, Roles, PrimaryApprovers, SecondaryApprovers, ManagedPrimaryUsers, ManagedSecondaryUsers',
      showAssignedToMe: 0,
      customFlag: 0,
    };

    // Fetch fresh user data
    this.usersService.getUsers(params);

    // If we have a selected filter manager, reload the staff list to show updated assignments
    if (this.selectedFilterManager) {
      // Small delay to ensure the fresh data is loaded before filtering
      setTimeout(() => {
        this.loadStaffFromManager(this.selectedFilterManager);
      }, 500);
    } else if (this.selectedFilterSecondaryManager) {
      // Small delay to ensure the fresh data is loaded before filtering
      setTimeout(() => {
        this.loadStaffFromSecondaryManager(this.selectedFilterSecondaryManager);
      }, 500);
    }
  };

  showBuildingAssignmentResults = (
    successCount: number,
    alreadyAssignedCount: number
  ) => {
    let message = '';

    if (successCount > 0 && alreadyAssignedCount > 0) {
      message = `${successCount} staff added to "${this.selectedNewBuilding.buildingName}". ${alreadyAssignedCount} already assigned.`;
    } else if (successCount > 0) {
      message = `Successfully added ${successCount} staff member(s) to "${this.selectedNewBuilding.buildingName}".`;
    } else if (alreadyAssignedCount > 0) {
      message = `All selected staff are already assigned to "${this.selectedNewBuilding.buildingName}".`;
    } else {
      message = 'Building assignment failed. Please try again.';
    }

    this.toastService.setToast({
      text: message,
      type: successCount > 0 ? 'success' : 'warn',
      icon: true,
    });

    // Clear selections and refresh the staff list if successful
    if (successCount > 0) {
      this.clearSelectionsAndRefresh();
    }
  };

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
